import { useEffect, useMemo, useState } from 'react';
import {
  clearCart,
  getCart,
  removeItemFromCart,
  subscribeToCart,
  updateItemQuantity,
} from '../../services/cart';
import { useLanguage } from '../../context/LanguageContext';

const formatCurrency = (value) =>
  `$${Number(value || 0).toFixed(2)}`;

const CartPage = () => {
  const [cart, setCart] = useState(() => getCart());
  const [pendingRemoval, setPendingRemoval] = useState(null);
  const items = Array.isArray(cart.items) ? cart.items : [];
  const { t } = useLanguage();
  const removalTitleId = 'cart-remove-modal-title';
  const removalDescriptionId = 'cart-remove-modal-description';

  useEffect(() => {
    const unsubscribe = subscribeToCart((nextCart) => setCart(nextCart));
    return unsubscribe;
  }, []);

  const getQuantity = (item) =>
    Math.max(1, Number(item.quantity || 1));

  const subtotal = useMemo(
    () =>
      items.reduce(
        (sum, item) =>
          sum + Number(item.priceUnit || 0) * getQuantity(item),
        0
      ),
    [items]
  );

  const shipping = useMemo(
    () =>
      items.reduce(
        (sum, item) =>
          sum + Number(item.shipping || 0) * getQuantity(item),
        0
      ),
    [items]
  );

  const total = subtotal + shipping;

  const handleQtyChange = (item, delta) => {
    const currentQty = getQuantity(item);
    if (delta < 0 && currentQty <= 1) {
      setPendingRemoval(item);
      return;
    }
    const nextQty = Math.max(1, currentQty + delta);
    const updated = updateItemQuantity(item.productId, item.variantId, nextQty);
    setCart(updated);
  };

  const performRemoval = (item) => {
    const updated = removeItemFromCart(item.productId, item.variantId);
    setCart(updated);
  };

  const handleCancelRemoval = () => setPendingRemoval(null);

  const handleConfirmRemoval = () => {
    if (!pendingRemoval) return;
    performRemoval(pendingRemoval);
    setPendingRemoval(null);
  };

  const handleClear = () => {
    const updated = clearCart();
    setCart(updated);
    setPendingRemoval(null);
  };

  const proceedToCheckout = () => {
    if (!items.length) return;
    if (typeof window === 'undefined') return;
    window.sessionStorage.setItem('pendingCartItems', JSON.stringify(items));
    window.location.assign('/checkout');
  };

  if (!items.length) {
    return (
      <div className="cart-page cart-page--empty">
        <h1>{t('cart.empty_title')}</h1>
        <p>{t('cart.empty_description')}</p>
        <button
          type="button"
          className="button"
          onClick={() => {
            if (typeof window !== 'undefined') {
              window.location.assign('/categories');
            }
          }}
        >
          {t('cart.empty_button')}
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="cart-page">
        <section className="cart-page__list">
        <header className="cart-page__header">
          <div>
            <p className="eyebrow">{t('cart.eyebrow')}</p>
            <h3>
              {items.length}{' '}
              {t(items.length === 1 ? 'cart.item' : 'cart.items')}
            </h3>
          </div>
          <button type="button" className="link-button" onClick={handleClear}>
            {t('cart.clear')}
          </button>
        </header>

        <div className="cart-lines">
          {items.map((item) => (
            <article className="cart-line" key={item.key || `${item.productId}:${item.variantId}`}>
              <div className="cart-line__media">
                {item.image ? (
                  <img src={item.image} alt={item.name} />
                ) : (
                  <span>{t('cart.no_image')}</span>
                )}
              </div>
              <div className="cart-line__body">
                <div className="cart-line__row">
                  <div>
                    <h3>{item.name}</h3>
                    {item.variantLabel && (
                      <p className="cart-line__variant">{item.variantLabel}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => setPendingRemoval(item)}
                  >
                    {t('cart.remove')}
                  </button>
                </div>
                <div className="cart-line__price">
                  {item.discount ? (
                    <>
                      <span className="old">{formatCurrency(item.originalPrice)}</span>
                      <span className="current">{formatCurrency(item.priceUnit)}</span>
                    </>
                  ) : (
                    <span className="current">{formatCurrency(item.priceUnit)}</span>
                  )}
                </div>
                {item.shipping ? (
                  <p className="cart-line__shipping">
                    {t('cart.shipping_prefix')} {formatCurrency(item.shipping)}
                  </p>
                ) : null}
                <div className="cart-line__qty">
                  <button
                    type="button"
                    onClick={() => handleQtyChange(item, -1)}
                    aria-label={t('cart.decrease')}
                  >
                    -
                  </button>
                  <span>{item.quantity}</span>
                  <button
                    type="button"
                    onClick={() => handleQtyChange(item, 1)}
                    aria-label={t('cart.increase')}
                  >
                    +
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <aside className="cart-page__summary">
        <h2>{t('cart.order_summary')}</h2>
        <div className="cart-summary__row">
          <span>{t('cart.subtotal')}</span>
          <strong>{formatCurrency(subtotal)}</strong>
        </div>
        <div className="cart-summary__row">
          <span>{t('cart.shipping')}</span>
          <strong>{formatCurrency(shipping)}</strong>
        </div>
        <div className="cart-summary__row cart-summary__total">
          <span>{t('cart.total')}</span>
          <strong>{formatCurrency(total)}</strong>
        </div>
        <button type="button" className="button" onClick={proceedToCheckout}>
          {t('cart.checkout')}
        </button>
        </aside>
      </div>

      {pendingRemoval && (
        <div
          className="cart-remove-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby={removalTitleId}
          aria-describedby={removalDescriptionId}
        >
          <div
            className="cart-remove-modal__backdrop"
            onClick={handleCancelRemoval}
          />
          <div className="cart-remove-modal__panel">
            <h3 id={removalTitleId}>{t('cart.remove_confirm_title')}</h3>
            <p
              className="cart-remove-modal__message"
              id={removalDescriptionId}
            >
              {t('cart.remove_confirm_description')}
            </p>
            {pendingRemoval?.name ? (
              <div className="cart-remove-modal__item">
                <span>{pendingRemoval.name}</span>
              </div>
            ) : null}
            <div className="cart-remove-modal__actions">
              <button
                type="button"
                className="button button--ghost"
                onClick={handleCancelRemoval}
              >
                {t('cart.remove_confirm_cancel')}
              </button>
              <button
                type="button"
                className="button button--danger"
                onClick={handleConfirmRemoval}
              >
                {t('cart.remove_confirm_confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CartPage;
